import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import subscriptionApi from '../api/subscriptionApi';
import { PaymentProvider, PlanOption, PlanType, Subscription, LoadingStatus } from '../../types';

interface SubscriptionState {
  activeSub: Subscription | null;
  plans: PlanOption[];
  status: LoadingStatus;
  paymentStatus: LoadingStatus;
  error: string | null;
}

const initialState: SubscriptionState = {
  activeSub: null,
  plans: [],
  status: 'idle',
  paymentStatus: 'idle',
  error: null,
};

export const fetchMySubscription = createAsyncThunk(
  'subscription/fetchMine',
  async (_, { rejectWithValue }) => {
    try {
      const res = await subscriptionApi.getMySubscription();
      return res.data as Subscription | null;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch subscription');
    }
  },
);

export const fetchPlans = createAsyncThunk('subscription/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    const res = await subscriptionApi.getPlans();
    return res.data as PlanOption[];
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to fetch plans');
  }
});

export const initiatePayment = createAsyncThunk(
  'subscription/initiatePayment',
  async (
    { planType, paymentProvider }: { planType: PlanType; paymentProvider: PaymentProvider },
    { rejectWithValue },
  ) => {
    try {
      const res = await subscriptionApi.initiatePayment(planType, paymentProvider);
      return res.data as { redirectUrl: string; paymentReference: string };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to initiate payment');
    }
  },
);

export const fetchPaymentStatus = createAsyncThunk(
  'subscription/fetchPaymentStatus',
  async (reference: string, { rejectWithValue }) => {
    try {
      const res = await subscriptionApi.getPaymentStatus(reference);
      return res.data as { status: string; planType: PlanType; endDate?: string };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch payment status');
    }
  },
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubError(state) { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMySubscription.fulfilled, (state, { payload }) => {
        state.activeSub = payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMySubscription.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchMySubscription.rejected, (state, { payload }) => {
        state.error = payload as string;
        state.status = 'failed';
      })
      .addCase(fetchPlans.fulfilled, (state, { payload }) => {
        state.plans = payload;
      })
      .addCase(initiatePayment.pending, (state) => { state.paymentStatus = 'loading'; })
      .addCase(initiatePayment.fulfilled, (state) => { state.paymentStatus = 'succeeded'; })
      .addCase(initiatePayment.rejected, (state, { payload }) => {
        state.error = payload as string;
        state.paymentStatus = 'failed';
      });
  },
});

export const { clearSubError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;

export const selectActiveSub    = (state: { subscription: SubscriptionState }) => state.subscription.activeSub;
export const selectPlans        = (state: { subscription: SubscriptionState }) => state.subscription.plans;
export const selectSubStatus    = (state: { subscription: SubscriptionState }) => state.subscription.status;
export const selectPaymentStatus = (state: { subscription: SubscriptionState }) => state.subscription.paymentStatus;

export const selectHasProAccess = (state: { subscription: SubscriptionState }): boolean => {
  const sub = state.subscription.activeSub;
  if (!sub) return false;
  if (sub.status !== 'active') return false;
  if (!sub.endDate) return false;
  return new Date(sub.endDate) > new Date();
};
